import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CaretRight,
  Student,
  Pill,
  ShieldCheck,
  Clock,
  Folders,
  Envelope,
  IdentificationBadge,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { listChildrenForParent } from "@/lib/dailyReports";
import ChildAvatar from "@/components/children/ChildAvatar";
import ChildProfilePhotoEditor from "@/components/children/ChildProfilePhotoEditor";
import ClearanceChip from "@/components/compliance/ClearanceChip";

/*
 * Family — every child in one place, plus the per-child tools that used
 * to crowd the Home page: photo, pickup, medications, health records,
 * conferences, and documents.
 */

const CHILD_LINKS = [
  { to: "/parent/medications", icon: Pill, label: "Medications" },
  { to: "/parent/health-records", icon: ShieldCheck, label: "Health" },
  { to: "/parent/pickup", icon: IdentificationBadge, label: "Pickup" },
  { to: "/parent/conferences", icon: Clock, label: "Conferences" },
  { to: "/parent/documents", icon: Folders, label: "Documents" },
  { to: "/parent/preferences", icon: Envelope, label: "Preferences" },
];

export default function ParentFamily() {
  const { profile } = useAuth();
  const [kids, setKids] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!profile?.id) return;
    (async () => {
      try {
        const list = await listChildrenForParent(profile.id);
        setKids(list);
      } finally {
        setLoaded(true);
      }
    })();
  }, [profile?.id]);

  return (
    <div data-testid="parent-family" className="space-y-5 sm:space-y-6">
      <section>
        <p className="overline text-primary-hover">Family</p>
        <h1 className="mt-2 font-heading text-2xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
          Your children
        </h1>
        <p className="mt-1.5 text-sm text-neutral-600 sm:mt-2 sm:text-base">
          Profiles, health, pickup, and paperwork — all in one place.
        </p>
      </section>

      {loaded && kids.length === 0 && (
        <Link
          to="/parent/enrollment"
          data-testid="family-empty-no-child"
          className="block rounded-2xl border border-primary/20 bg-primary/5 p-5 transition-all hover:-translate-y-[2px] hover:shadow-md active:scale-[0.99]"
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary-hover">
            Welcome
          </p>
          <p className="mt-1 font-heading text-base font-semibold tracking-tight text-neutral-900">
            Let's get your little one set up
          </p>
          <p className="mt-1 text-sm leading-relaxed text-neutral-600">
            We don't have a child on file for your family yet. You can start an
            inquiry or check the status of an existing one.
          </p>
          <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary-hover">
            Open enrollment
            <CaretRight size={12} weight="bold" />
          </span>
        </Link>
      )}

      {kids.map((kid) => (
        <article
          key={kid.id}
          data-testid={`family-child-card-${kid.id}`}
          className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-[0_2px_12px_rgba(20,22,26,0.04)]"
        >
          <div className="flex min-w-0 items-center gap-4">
            <ChildAvatar
              path={kid.profile_photo_path}
              name={`${kid.first_name || ""} ${kid.last_name || ""}`}
              size={56}
              testId={`family-child-avatar-${kid.id}`}
            />
            <div className="min-w-0">
              {kid.classroom?.name && (
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
                  {kid.classroom.name}
                </p>
              )}
              <h2 className="mt-0.5 font-heading text-xl font-semibold tracking-tight text-neutral-900">
                {kid.first_name} {kid.last_name || ""}
              </h2>
              <ClearanceChip
                childId={kid.id}
                resolveHref="/parent/health-records"
                resolveLabel="Open health records"
                testIdPrefix={`family-clearance-${kid.id}`}
              />
            </div>
          </div>

          <div className="mt-5">
            <ChildProfilePhotoEditor
              child={kid}
              onChanged={(newPath) =>
                setKids((prev) =>
                  prev.map((k) =>
                    k.id === kid.id ? { ...k, profile_photo_path: newPath } : k
                  )
                )
              }
              testId={`family-child-photo-editor-${kid.id}`}
            />
          </div>

          <div className="mt-5 grid grid-cols-2 gap-2">
            {CHILD_LINKS.map(({ to, icon: Icon, label }) => (
              <Link
                key={to}
                to={to}
                data-testid={`family-link-${label.toLowerCase().replace(/\s+/g, "-")}-${kid.id}`}
                className="flex items-center gap-2.5 rounded-xl bg-neutral-100/70 px-3 py-3 text-sm font-medium text-neutral-800 transition-all hover:bg-neutral-200/70 active:scale-[0.99]"
              >
                <Icon size={18} weight="duotone" className="shrink-0 text-primary-hover" />
                <span className="truncate">{label}</span>
              </Link>
            ))}
          </div>
        </article>
      ))}

      {loaded && kids.length > 0 && (
        <Link
          to="/parent/enrollment"
          data-testid="parent-open-enrollment"
          className="flex items-center gap-4 rounded-2xl border border-neutral-200 bg-white p-5 transition-all hover:-translate-y-[2px] hover:shadow-md active:scale-[0.99]"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Student size={22} weight="duotone" className="text-primary-hover" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-base font-semibold tracking-tight text-neutral-900">
              Enroll another child
            </p>
            <p className="text-sm text-neutral-500">
              Submit a new inquiry or check an existing application's status.
            </p>
          </div>
          <CaretRight size={18} weight="bold" className="text-neutral-400" />
        </Link>
      )}
    </div>
  );
}
